import { useCallback, useState } from 'react';
import {
  AudioFileFormat,
  ScriptFormat,
} from '../../../server/graphql/__generated.types';
import useDownloadClips, { ActorRelatedMetadata } from './useDownloadClips';
import useDownloadProjectClips from './useDownloadProjectClips';
import useDownloadTakes from './useDownloadTakes';
import useDownloadProjectTakes from './useDownloadProjectTakes';

type downloadClipsProps = {
  clipIds: string[];
  audioFileFormat: AudioFileFormat;
  actorRelatedMetadata?: ActorRelatedMetadata;
  includeScriptFormats?: Array<ScriptFormat>;
};

type downloadProjectClipsProps = {
  projectId: string;
  audioFileFormat: AudioFileFormat;
  includeScriptFormats?: Array<ScriptFormat>;
};

type downloadTakesProps = {
  takeIds: string[];
  audioFileFormat: AudioFileFormat;
  includeScriptFormats?: Array<ScriptFormat>;
  source: 'section' | 'modal' | 'bulk';
  projectId: string;
  filename?: string | null;
  includeProjectName?: boolean;
  numberFilesAutomatically?: boolean;
  msGapDuration?: number;
  showTimeoutPrompt?: boolean;
};

type downloadProjectTakesProps = {
  projectId: string;
  audioFileFormat: AudioFileFormat;
  takeCount: number;
  includeScriptFormats?: Array<ScriptFormat>;
  filename?: string | null;
  includeProjectName?: boolean;
  numberFilesAutomatically?: boolean;
  msGapDuration?: number;
  showTimeoutPrompt?: boolean;
};

type HandleDownloadClips = (inputs: downloadClipsProps) => Promise<any>;

type HandleDownloadTakes = (inputs: downloadTakesProps) => Promise<any>;

type HandleDownloadProjectClips = (
  inputs: downloadProjectClipsProps
) => Promise<any>;

type HandleDownloadProjectTakes = (
  inputs: downloadProjectTakesProps
) => Promise<any>;

function useClipDownloader(props: {
  onDownloadStart?: () => void;
  onDownloadComplete?: () => void;
  setDownloadError?: (downloadError: string) => void;
  onCombineDownloadError?: () => void;
}): {
  onDownloadClips: HandleDownloadClips;
  onDownloadProjectClips: HandleDownloadProjectClips;
  onDownloadTakes: HandleDownloadTakes;
  onDownloadProjectTakes: HandleDownloadProjectTakes;
  downloading: boolean;
} {
  const {
    onDownloadStart,
    onDownloadComplete,
    setDownloadError,
    onCombineDownloadError,
  } = props;
  const [downloading, setDownloading] = useState<boolean>(false);
  const download = useDownloadClips();
  const downloadProject = useDownloadProjectClips();
  const downloadTakes = useDownloadTakes();
  const downloadProjectTakes = useDownloadProjectTakes();

  const handleDownloadClips = useCallback(
    async (inputs: {
      clipIds: string[];
      audioFileFormat: AudioFileFormat;
      actorRelatedMetadata?: ActorRelatedMetadata;
      includeScriptFormats?: Array<ScriptFormat>;
    }) => {
      const {
        clipIds,
        audioFileFormat,
        actorRelatedMetadata,
        includeScriptFormats,
      } = inputs;
      setDownloading(true);
      if (onDownloadStart) {
        onDownloadStart();
      }

      await download({
        audioFileFormat,
        clipIds,
        actorRelatedMetadata,
        includeScriptFormats,
        setDownloadError,
      });

      setDownloading(false);
      if (onDownloadComplete) {
        onDownloadComplete();
      }
    },
    [download, onDownloadComplete, onDownloadStart, setDownloadError]
  );

  const handleDownloadProjectClips = useCallback(
    async (inputs: {
      projectId: string;
      audioFileFormat: AudioFileFormat;
      includeScriptFormats?: Array<ScriptFormat>;
    }) => {
      const { projectId, audioFileFormat, includeScriptFormats } = inputs;

      setDownloading(true);
      if (onDownloadStart) {
        onDownloadStart();
      }

      downloadProject({
        projectId,
        audioFileFormat,
        includeScriptFormats,
      });

      setDownloading(false);
      if (onDownloadComplete) {
        onDownloadComplete();
      }
    },
    [downloadProject, onDownloadComplete, onDownloadStart]
  );

  const handleDownloadTakes = useCallback(
    async (inputs: downloadTakesProps) => {
      const {
        takeIds,
        audioFileFormat,
        includeScriptFormats,
        source,
        projectId,
        filename,
        includeProjectName,
        numberFilesAutomatically,
        msGapDuration,
        showTimeoutPrompt,
      } = inputs;

      setDownloading(true);
      onDownloadStart?.();

      await downloadTakes({
        takeIds,
        audioFileFormat,
        includeScriptFormats,
        setDownloadError,
        source,
        projectId,
        filename,
        includeProjectName,
        numberFilesAutomatically,
        msGapDuration,
        onCombineDownloadError,
        showTimeoutPrompt: Boolean(msGapDuration) || showTimeoutPrompt,
      });

      setDownloading(false);
      onDownloadComplete?.();
    },
    [
      downloadTakes,
      onDownloadComplete,
      onDownloadStart,
      setDownloadError,
      onCombineDownloadError,
    ]
  );

  const handleDownloadProjectTakes = useCallback(
    async (inputs: downloadProjectTakesProps) => {
      const {
        projectId,
        audioFileFormat,
        includeScriptFormats,
        filename,
        includeProjectName,
        numberFilesAutomatically,
        msGapDuration,
        takeCount,
        showTimeoutPrompt,
      } = inputs;

      setDownloading(true);
      if (onDownloadStart) {
        onDownloadStart();
      }

      await downloadProjectTakes({
        projectId,
        audioFileFormat,
        includeScriptFormats,
        setDownloadError,
        filename,
        includeProjectName,
        numberFilesAutomatically,
        msGapDuration,
        takeCount,
        onCombineDownloadError,
        showTimeoutPrompt: Boolean(msGapDuration) || showTimeoutPrompt,
      });

      setDownloading(false);
      if (onDownloadComplete) {
        onDownloadComplete();
      }
    },
    [
      onDownloadStart,
      downloadProjectTakes,
      onDownloadComplete,
      setDownloadError,
      onCombineDownloadError,
    ]
  );

  return {
    onDownloadClips: handleDownloadClips,
    onDownloadProjectClips: handleDownloadProjectClips,
    onDownloadTakes: handleDownloadTakes,
    onDownloadProjectTakes: handleDownloadProjectTakes,
    downloading,
  };
}

export default useClipDownloader;
